<template>
  <div class="dashboardWrapper">
    <CustomDashboard 
      :key="partner?._id" 
      :iframe="iframe" 
    />
  </div>
</template>

<script>
import CustomDashboard from './CustomDashboard'
import { mapGetters } from 'vuex'
import { FEATURE_FLAGS } from '@/helpers/featureFlags'

export default {
  name: 'DashboardWrapper',

  components: {
    CustomDashboard
  },

  data: () => ({
    iframe: null,
    forceReload: false,
    isPartnerChanged: false
  }),

  watch: {
    '$route': {
      handler(to, from) {
        if (to.params.title !== from.params.title) {
          this.isPartnerChanged = false
        }
        // forces reload when route changes to dashboard
        if (to.path.includes('/dashboard') && (!from || from.path !== to.path || from.query.dashboard !== to.query.dashboard)) {
          this.forceReload = true
        }
      },
      immediate: false
    },
    '$route.params': 'loadCustomDashboard',
    '$route.params.title': 'redirectToCurrentDashboardPathByTitle',
    user: {
      handler(newUser, oldUser) {
        // reloads dashboard when user changes (e.g.: partner switch)
        if (this.hasUserChanged(newUser, oldUser)) {
          this.forceReload = true
        }
        this.getDashboardsPath()
      },
      deep: true
    },
    partner: {
      handler(newPartner, oldPartner) {
        this.isPartnerChanged = newPartner?._id !== oldPartner?._id
      },
      immediate: false
    }
  },

  created() {
    this.getDashboardsPath()
  },

  computed: {
    ...mapGetters({
      user: 'getUser',
      partner: 'getPartner'
    }),
    hasDashboards() {
      return !!this.user?.dashboards?.length
    },
    currentDashboardPath() {
      const currentPath = this.$route?.params?.title && !this.isPartnerChanged
      ? this.$route.params.title
      : this.user.dashboards[0]?.name

      return currentPath
    },
    isIndicatorsDashboardFlagActive() {
      return this.$isFeatureFlagActive(FEATURE_FLAGS.IndicatorsDashboard)
    }
  },

  methods: {
    hasUserChanged(newUser, oldUser) {
      if (!oldUser) return false

      const userIdChanged = newUser?._id !== oldUser?._id
      const partnerChanged = newUser?.partner !== oldUser?.partner

      return userIdChanged || partnerChanged
    },

    getDashboardsPath() {
      if (!this.user.powerbiPermission || !this.hasDashboards) {
        return this.redirectToIndicators()
      }

      const targetPath = `/partners/dashboard/${this.currentDashboardPath}`
      const targetQuery = { dashboard: this.currentDashboardPath }

      if (this.isSameRoute(targetPath, targetQuery)) {
        return this.handleSameRoute(targetPath, targetQuery)
      }

      return this.navigateToDashboard(targetPath, targetQuery)
    },

    redirectToIndicators() {
      const indicatorsContainerPath = this.isIndicatorsDashboardFlagActive ? 'loyalty' : 'partners'
      const targetPath = `${indicatorsContainerPath}/indicators`
      const shouldRedirect = this.$route.path !== targetPath || this.forceReload

      this.forceReload = false
      if (!shouldRedirect) return

      return this.$router.push(targetPath)
    },

    isSameRoute(targetPath, targetQuery) {
      const currentPath = this.$route.path
      const currentQuery = this.$route.query
      const isSamePath = currentPath === targetPath
      const isSameQuery = currentQuery.dashboard === targetQuery.dashboard

      return isSamePath && isSameQuery
    },

    handleSameRoute(targetPath, targetQuery) {
      if (!this.forceReload) {
        this.forceReload = false
        return
      }

      return this.forceReloadDashboard(targetPath, targetQuery)
    },

    forceReloadDashboard(targetPath, targetQuery) {
      this.forceReload = false
      this.$showLoading()

      this.$nextTick(() => {
        this.$router.push({
          path: targetPath,
          query: targetQuery
        }).then(() => {
          setTimeout(() => this.$hideLoading(), 2000)
        }).catch(() => {
          this.$hideLoading()
        })
      })
    },

    navigateToDashboard(targetPath, targetQuery) {
      this.forceReload = false
      this.$showLoading()

      setTimeout(() => this.$hideLoading(), 2000)

      return this.$router.push({
        path: targetPath,
        query: targetQuery
      }).catch(() => {
        // ignores redundant navigation error
      })
    },
    redirectToCurrentDashboardPathByTitle() {
      const targetPath = `/partners/dashboard/${this.currentDashboardPath}`
      const targetQuery = { dashboard: this.currentDashboardPath }
      const currentPath = this.$route.path
      const currentQuery = this.$route.query

      const isSamePath = currentPath === targetPath
      const isSameQuery = currentQuery.dashboard === targetQuery.dashboard

      if (isSamePath && isSameQuery) return

      this.$router.push({
        path: targetPath,
        query: targetQuery
      }).catch(() => {
        // ignores redundant navigation error
      })
    },
    loadCustomDashboard() {
      if (this.$attrs?.iframe) {
        this.iframe = this.$attrs.iframe
      }
    }
  }
}
</script>
